import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { FaCamera, FaWeight, FaRuler, FaCalendarWeek, FaStar, FaComment, FaPlus, FaChevronDown, FaChevronUp, FaArrowLeft, FaEdit, FaTimes } from 'react-icons/fa';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useGender } from '@/context/GenderContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Comprimir imagen antes de subir (reduce 5-15MB a ~200KB)
const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve) => {
    // Si el archivo es menor a 500KB, no comprimir
    if (file.size < 500 * 1024) {
      resolve(file);
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Redimensionar manteniendo proporción
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

export default function MyProgress() {
  const navigate = useNavigate();
  const { isMale, theme, getText } = useGender();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState(null);
  
  // Edit mode state
  const [editingEntry, setEditingEntry] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editPhotos, setEditPhotos] = useState([]);
  const [editPhotoPreview, setEditPhotoPreview] = useState([]);
  
  // Colores dinámicos según género - Electric Obsidian theme
  const gradientBg = 'from-[#0a0a0a] to-[#111111]';
  const gradientHeader = 'from-[var(--brand-primary)] to-[var(--brand-primary-strong)]';
  const gradientButton = 'from-[var(--brand-primary)] to-[var(--brand-primary-strong)] hover:from-[var(--brand-primary-strong)] hover:to-[var(--brand-primary)]';
  const accentColor = 'pink';
  const secondaryColor = 'rose';
  
  // Form state
  const [weekNumber, setWeekNumber] = useState(1);
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [completionRate, setCompletionRate] = useState(80);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('deisycfit_token');
      const response = await axios.get(`${API}/progress/my-entries`, { 
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      setEntries(response.data.entries || []);
      // Set next week number
      if (response.data.entries && response.data.entries.length > 0) {
        const maxWeek = Math.max(...response.data.entries.map(e => e.week_number));
        setWeekNumber(maxWeek + 1);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      // Comprimir imagen antes de agregar
      const compressed = await compressImage(file);
      setPhotos(prev => [...prev, compressed]);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(compressed);
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!weight || !height) {
      toast.error('Por favor completa peso y estatura');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('week_number', weekNumber);
      formData.append('weight', weight);
      formData.append('height', height);
      formData.append('completion_rate', completionRate);
      formData.append('notes', notes);
      
      photos.forEach(photo => {
        formData.append('photos', photo);
      });

      // Obtener token de autenticación
      const token = localStorage.getItem('deisycfit_token');
      
      await axios.post(`${API}/progress/entry`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      toast.success('¡Progreso guardado exitosamente! 💪');
      
      // Reset form
      setWeekNumber(prev => prev + 1);
      setWeight('');
      setHeight('');
      setCompletionRate(80);
      setNotes('');
      setPhotos([]);
      setPhotoPreview([]);
      setShowForm(false);
      
      fetchEntries();
    } catch (error) {
      console.error('Error saving progress:', error);
      if (error.response?.status === 401) {
        toast.error('Tu sesión ha expirado. Por favor inicia sesión de nuevo.');
      } else if (error.response?.status === 413) {
        toast.error('Las fotos son muy grandes. Intenta con fotos más pequeñas.');
      } else if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else if (error.message?.includes('Network')) {
        toast.error('Error de conexión. Verifica tu internet e intenta de nuevo.');
      } else {
        toast.error('Error al guardar el progreso. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Edit entry functions
  const startEditing = (entry) => {
    setEditingEntry(entry.id);
    setEditFormData({
      week_number: entry.week_number,
      weight: entry.weight,
      height: entry.height,
      completion_rate: entry.completion_rate,
      notes: entry.notes || ''
    });
    setEditPhotos([]);
    setEditPhotoPreview([]);
    setExpandedWeek(entry.id);
  };

  const cancelEditing = () => {
    setEditingEntry(null);
    setEditFormData({});
    setEditPhotos([]);
    setEditPhotoPreview([]);
  };

  const handleEditPhotoChange = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      const compressed = await compressImage(file);
      setEditPhotos(prev => [...prev, compressed]);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(compressed);
    }
  };

  const removeEditPhoto = (index) => {
    setEditPhotos(prev => prev.filter((_, i) => i !== index));
    setEditPhotoPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.weight || !editFormData.height) {
      toast.error('Por favor completa peso y estatura');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('week_number', editFormData.week_number);
      formData.append('weight', editFormData.weight);
      formData.append('height', editFormData.height);
      formData.append('completion_rate', editFormData.completion_rate);
      formData.append('notes', editFormData.notes);
      
      editPhotos.forEach(photo => {
        formData.append('photos', photo);
      });

      const token = localStorage.getItem('deisycfit_token');
      
      await axios.put(`${API}/progress/entry/${editingEntry}`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      toast.success('¡Progreso actualizado! ✨');
      cancelEditing();
      fetchEntries();
    } catch (error) {
      console.error('Error updating progress:', error);
      if (error.response?.status === 401) {
        toast.error('Tu sesión ha expirado. Por favor inicia sesión de nuevo.');
      } else if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error al actualizar el progreso.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradientBg} flex items-center justify-center`}>
        <div className={`animate-spin rounded-full h-12 w-12 border-4 border-${accentColor}-500 border-t-transparent`}></div>
      </div>
    );
  }

  // Detectar si es la primera vez subiendo fotos (no hay entradas previas con fotos)
  const hasAnyPhotos = entries.some(e => Array.isArray(e.photos) && e.photos.length > 0);
  const isFirstTimePhotos = !hasAnyPhotos;

  // Detectar si toca hacer nuevo upload semanal (>=7 días desde el último)
  const latestPhotoEntry = entries.filter(e => Array.isArray(e.photos) && e.photos.length > 0)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  let daysSinceLastPhotos = null;
  if (latestPhotoEntry?.created_at) {
    const diffMs = Date.now() - new Date(latestPhotoEntry.created_at).getTime();
    daysSinceLastPhotos = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }
  const needsWeeklyUpload = !isFirstTimePhotos && daysSinceLastPhotos !== null && daysSinceLastPhotos >= 7;
  // Título dinámico
  const pageTitle = isFirstTimePhotos
    ? '📸 Sube tus fotos iniciales'
    : `📊 Fotos Semana ${weekNumber}`;
  const pageSubtitle = isFirstTimePhotos
    ? getText(
        'Estas fotos son el punto de partida para medir tu progreso, Guerrero. Súbelas antes de que liberemos tu plan.',
        'Estas fotos son el punto de partida para medir tu progreso, Guerrera. Súbelas antes de que liberemos tu plan.'
      )
    : getText('Registra tu avance semana a semana, Guerrero', 'Registra tu avance semana a semana, Guerrera');

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradientBg} pb-20`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradientHeader} text-white py-8 px-6`}>
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate('/dashboard')}
          >
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          <h1 className="text-3xl font-bold" data-testid="my-progress-title">{pageTitle}</h1>
          <p className="text-white/80 mt-2">
            {pageSubtitle}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Banner de recordatorio semanal (solo si ya subió antes y pasó 7+ días) */}
        {needsWeeklyUpload && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="weekly-reminder-banner"
            className="mb-6 rounded-2xl p-5 border-2 flex items-start gap-4"
            style={{
              background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.15), rgba(245, 158, 11, 0.1))',
              borderColor: 'rgba(var(--brand-primary-glow), 0.4)',
            }}
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--brand-primary-glow), 0.25)' }}>
              <span className="text-2xl">📸</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-pink-300">
                ¡Es hora de subir tus fotos de la Semana {weekNumber}!
              </h3>
              <p className="text-sm text-gray-300 mt-1">
                Han pasado {daysSinceLastPhotos} días desde tu último registro. Adjunta tus 4 fotos + cómo te fue en la semana (cumplimiento del plan, cómo te sentiste con las comidas y el entrenamiento).
              </p>
              <p className="text-xs text-pink-200 mt-2 italic">
                💡 Mismas condiciones de luz y ropa que las fotos iniciales para comparar tu progreso.
              </p>
            </div>
          </motion.div>
        )}

        {/* Foto de referencia — solo la primera vez para guiar cómo tomarse las fotos */}
        {isFirstTimePhotos && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 rounded-2xl p-6 border-2"
            data-testid="initial-photos-reference"
            style={{
              background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.08), rgba(var(--brand-primary-glow), 0.04))',
              borderColor: 'rgba(var(--brand-primary-glow), 0.35)',
            }}
          >
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0 w-full md:w-64">
                <img
                  src={isMale
                    ? "/assets/fit-evolution/initial-photos-reference-male.jpeg"
                    : "/assets/fit-evolution/initial-photos-reference.jpeg"}
                  alt="Ejemplo de cómo tomar tus fotos iniciales"
                  className="w-full h-auto rounded-xl border-2 shadow-lg"
                  style={{ borderColor: 'rgba(var(--brand-primary-glow), 0.4)' }}
                />
                <p className="text-[10px] mt-2 italic text-center" style={{ color: 'var(--brand-primary)' }}>
                  {isMale
                    ? 'Fotos de referencia · tómatelas como más cómodo te sientas'
                    : 'Ejemplo · así deben tomarse las 4 fotos'}
                </p>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--brand-primary)' }}>
                  📷 Cómo tomarte las fotos
                </h3>
                {isMale ? (
                  <>
                    <p className="text-sm text-gray-300 mb-3 leading-relaxed">
                      Estas son fotos de <b>referencia</b>, puedes tomártelas como más cómodo te sientas.
                      Por favor adjunta como mínimo <b>foto de frente y de espalda</b>. También puedes
                      adjuntar foto de lado si quieres un seguimiento más completo.
                    </p>
                    <ul className="text-sm text-gray-300 space-y-2 leading-relaxed">
                      <li>1️⃣ <b>Frente</b> — brazos relajados a los lados, torso descubierto.</li>
                      <li>2️⃣ <b>Espalda</b> — de espaldas, misma postura.</li>
                      <li>3️⃣ <b>Perfil (opcional)</b> — de lado, para ver postura.</li>
                    </ul>
                    <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(var(--brand-primary-glow), 0.12)' }}>
                      <p className="text-xs" style={{ color: 'var(--brand-primary-soft)' }}>
                        <b>Son esenciales</b> para iniciar tu proceso — sin ellas no podemos medir tus avances reales.
                        Recuerda adjuntar tus fotos <b>cada semana</b> con la misma luz, fondo y ropa para
                        comparar honestamente cómo evoluciona tu físico. 💪
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <ul className="text-sm text-gray-300 space-y-2 leading-relaxed">
                      <li>1️⃣ <b>Frente completo</b> — brazos relajados a los lados.</li>
                      <li>2️⃣ <b>Perfil derecho</b> — de lado, mirando a la izquierda.</li>
                      <li>3️⃣ <b>Perfil izquierdo</b> — de lado, mirando a la derecha.</li>
                      <li>4️⃣ <b>Espalda</b> — de espaldas, en la misma postura.</li>
                    </ul>
                    <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(var(--brand-primary-glow), 0.12)' }}>
                      <p className="text-xs" style={{ color: 'var(--brand-primary-soft)' }}>
                        <b>Tips:</b> traje de baño o ropa interior, buena luz natural, fondo claro y liso. Mismas condiciones cada semana para comparar avances reales.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
        {/* Add New Entry Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            onClick={() => setShowForm(!showForm)}
            data-testid="my-progress-add-week-btn"
            className={`w-full bg-gradient-to-r ${gradientButton} text-white py-6 text-lg font-bold rounded-xl shadow-lg`}
          >
            <FaPlus className="mr-2" /> 
            {showForm
              ? 'Cancelar'
              : (isFirstTimePhotos
                  ? '📸 Subir mis fotos iniciales'
                  : `Agregar Semana ${weekNumber}`)}
          </Button>
        </motion.div>

        {/* New Entry Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8"
          >
            <Card className={`border-2 border-${accentColor}-200 shadow-xl`}>
              <CardHeader className={`bg-gradient-to-r from-${accentColor}-100 to-${secondaryColor}-100`}>
                <CardTitle className={`text-${accentColor}-600`}>📝 Nueva Entrada - Semana {weekNumber}</CardTitle>
                <CardDescription>Registra tu progreso de esta semana</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className={`grid ${isFirstTimePhotos ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <FaCalendarWeek className={`text-${secondaryColor}-500`} /> Semana
                      </Label>
                      <Input
                        type="number"
                        value={weekNumber}
                        onChange={(e) => setWeekNumber(parseInt(e.target.value))}
                        min="1"
                        max="52"
                        className="text-center text-xl font-bold"
                      />
                    </div>
                    {!isFirstTimePhotos && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <FaStar className="text-yellow-500" /> Cumplimiento (%)
                        </Label>
                        <Input
                          type="number"
                          value={completionRate}
                          onChange={(e) => setCompletionRate(parseInt(e.target.value))}
                          min="0"
                          max="100"
                          className="text-center text-xl font-bold"
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <FaWeight style={{ color: theme.primary }} /> Peso (kg)
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        placeholder="Ej: 65.5"
                        className="text-center text-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <FaRuler style={{ color: theme.secondary }} /> Estatura (cm)
                      </Label>
                      <Input
                        type="number"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                        placeholder="Ej: 165"
                        aria-invalid={height !== '' && !isNaN(parseFloat(height)) && parseFloat(height) < 100}
                        className={`text-center text-xl ${height !== '' && !isNaN(parseFloat(height)) && parseFloat(height) < 100 ? 'border-red-500 focus:border-red-500' : ''}`}
                        required
                      />
                      {height !== '' && !isNaN(parseFloat(height)) && parseFloat(height) < 100 && (
                        <p className="text-sm font-semibold text-red-500" data-testid="height-error">
                          Ingresa tu estatura en centímetros (ejemplo: 179)
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>📝 Notas / Comentarios</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="¿Cómo te sentiste esta semana? ¿Algún logro o dificultad?"
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <FaCamera className="text-green-500" /> Fotos del Progreso
                    </Label>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoChange}
                      className="cursor-pointer"
                    />
                    {photoPreview.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {photoPreview.map((preview, index) => (
                          <div key={index} className="relative">
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              className="w-20 h-20 object-cover rounded-lg border-2"
                              style={{ borderColor: `${theme.primary}40` }}
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-4 text-lg font-bold"
                  >
                    {submitting ? '⏳ Guardando...' : '💾 Guardar Progreso'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Progress Entries List */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            📅 Historial de Progreso
            <Badge variant="secondary">{entries.length} registros</Badge>
          </h2>

          {entries.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500 text-lg">Aún no tienes registros de progreso.</p>
              <p className="text-gray-400 mt-2">¡Comienza agregando tu primera semana! 💪</p>
            </Card>
          ) : (
            entries.map((entry) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card 
                  className="overflow-hidden border-l-4 hover:shadow-lg transition-shadow"
                  style={{ borderLeftColor: theme.primary }}
                >
                  <div 
                    className="p-4 cursor-pointer flex justify-between items-center"
                    style={{ background: `linear-gradient(to right, white, ${theme.primaryLight}20)` }}
                    onClick={() => setExpandedWeek(expandedWeek === entry.id ? null : entry.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="text-white w-14 h-14 rounded-xl flex flex-col items-center justify-center font-bold"
                        style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
                      >
                        <span className="text-xs">SEM</span>
                        <span className="text-xl">{entry.week_number}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-800">{entry.weight} kg</span>
                          <span className="text-gray-400">|</span>
                          <span className="text-gray-600">{entry.height} cm</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`${entry.completion_rate >= 80 ? 'bg-green-500' : entry.completion_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}>
                            {entry.completion_rate}% cumplimiento
                          </Badge>
                          {entry.photos && entry.photos.length > 0 && (
                            <Badge 
                              variant="outline" 
                              style={{ borderColor: `${theme.primary}60`, color: theme.primary }}
                            >
                              <FaCamera className="mr-1" /> {entry.photos.length} fotos
                            </Badge>
                          )}
                          {entry.admin_comments && entry.admin_comments.length > 0 && (
                            <Badge 
                              variant="outline" 
                              style={{ borderColor: `${theme.secondary}60`, color: theme.secondary }}
                            >
                              <FaComment className="mr-1" /> {entry.admin_comments.length}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {expandedWeek === entry.id ? <FaChevronUp /> : <FaChevronDown />}
                  </div>

                  {expandedWeek === entry.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="px-4 pb-4 border-t"
                    >
                      {/* Edit Mode */}
                      {editingEntry === entry.id ? (
                        <form onSubmit={handleEditSubmit} className="mt-4 space-y-4">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-semibold text-gray-700">✏️ Editar Entrada</h4>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm"
                              onClick={cancelEditing}
                              className="text-gray-500"
                            >
                              <FaTimes className="mr-1" /> Cancelar
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm">Peso (kg)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={editFormData.weight}
                                onChange={(e) => setEditFormData({...editFormData, weight: e.target.value})}
                                required
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Estatura (cm)</Label>
                              <Input
                                type="number"
                                value={editFormData.height}
                                onChange={(e) => setEditFormData({...editFormData, height: e.target.value})}
                                aria-invalid={editFormData.height !== '' && !isNaN(parseFloat(editFormData.height)) && parseFloat(editFormData.height) < 100}
                                className={editFormData.height !== '' && !isNaN(parseFloat(editFormData.height)) && parseFloat(editFormData.height) < 100 ? 'border-red-500 focus:border-red-500' : ''}
                                required
                              />
                              {editFormData.height !== '' && !isNaN(parseFloat(editFormData.height)) && parseFloat(editFormData.height) < 100 && (
                                <p className="text-sm font-semibold text-red-500 mt-1" data-testid="height-edit-error">
                                  Ingresa tu estatura en centímetros (ejemplo: 179)
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-sm">Cumplimiento (%)</Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={editFormData.completion_rate}
                              onChange={(e) => setEditFormData({...editFormData, completion_rate: parseInt(e.target.value)})}
                            />
                          </div>
                          
                          <div>
                            <Label className="text-sm">Notas</Label>
                            <Textarea
                              value={editFormData.notes}
                              onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                              rows={2}
                            />
                          </div>
                          
                          <div>
                            <Label className="text-sm flex items-center gap-2">
                              <FaCamera className="text-green-500" /> Agregar más fotos
                            </Label>
                            <Input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleEditPhotoChange}
                              className="cursor-pointer mt-1"
                            />
                            {editPhotoPreview.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {editPhotoPreview.map((preview, index) => (
                                  <div key={index} className="relative">
                                    <img
                                      src={preview}
                                      alt={`Nueva foto ${index + 1}`}
                                      className="w-16 h-16 object-cover rounded border"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeEditPhoto(index)}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                          >
                            {submitting ? '⏳ Guardando...' : '💾 Guardar Cambios'}
                          </Button>
                        </form>
                      ) : (
                        <>
                          {/* Edit Button */}
                          <div className="mt-4 flex justify-end">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => startEditing(entry)}
                              className="text-pink-600 border-pink-300 hover:bg-pink-50"
                            >
                              <FaEdit className="mr-2" /> Editar
                            </Button>
                          </div>
                          
                          {/* Notas */}
                          {entry.notes && (
                            <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                              <p className="text-sm text-gray-400 mb-1">📝 Mis notas:</p>
                              <p className="text-gray-300">{entry.notes}</p>
                            </div>
                          )}

                          {/* Fotos */}
                          {entry.photos && entry.photos.length > 0 && (
                            <div className="mt-4">
                              <p className="text-sm text-gray-500 mb-2">📸 Fotos:</p>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                {entry.photos.map((photo, idx) => (
                                  <a key={idx} href={photo} target="_blank" rel="noopener noreferrer" className="block">
                                    <img
                                      src={photo}
                                      alt={`Progreso semana ${entry.week_number}`}
                                      className="w-full h-32 object-contain rounded-lg border-2 hover:scale-105 transition-transform bg-gray-100"
                                      style={{ borderColor: `${theme.primary}40` }}
                                    />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Comentarios del Admin */}
                          {entry.admin_comments && entry.admin_comments.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <p className="text-sm text-gray-500 mb-2">💬 Comentarios de Deisy:</p>
                              {entry.admin_comments.map((comment) => (
                                <div 
                                  key={comment.id} 
                                  className="p-3 rounded-lg border-l-4"
                                  style={{ 
                                    backgroundColor: `${theme.secondary}10`, 
                                    borderLeftColor: theme.secondary 
                                  }}
                                >
                                  <p style={{ color: theme.secondary }}>{comment.text}</p>
                                  <p className="text-xs mt-1" style={{ color: `${theme.secondary}80` }}>
                                    {new Date(comment.created_at).toLocaleDateString('es-CO', { 
                                      day: 'numeric', 
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}

                          <p className="text-xs text-gray-400 mt-4">
                            Registrado el {new Date(entry.created_at).toLocaleDateString('es-CO', { 
                              weekday: 'long',
                              day: 'numeric', 
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </>
                      )}
                    </motion.div>
                  )}
                </Card>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
